import { createSelector } from 'reselect';
import { shippingGroupForId } from '../shipping/shippingSelectors';


export const filterDigital = lineItems => lineItems.filter(lineItem => lineItem.deliveryMethod === 'DIGITAL');
export const filterPhysical = lineItems => lineItems.filter(lineItem => lineItem.deliveryMethod === 'PHYSICAL');


export const getCardFee = lineItem => (
    lineItem.product
        ? lineItem.product.fees.filter((fee) => (
            Number(fee.amount) !== 0 && fee.name === "plasticActivationFeeAmount"
        )).reduce((amount,fee) => (
            amount + Number(fee.amount)
        )
        , 0)
        : 0
);

export const sumFees = lineItem => {
    return getCardFee(lineItem) * lineItem.quantity;
};

export const sumTotalFees = lineItems => lineItems.reduce((sum, lineItem) => sum + sumFees(lineItem), 0);

export const sumValues = lineItem => lineItem.quantity * lineItem.amount;

export const getShippingAmount = lineItem => (lineItem.shippingGroup && lineItem.shippingGroup.shippingMethod ? lineItem.shippingGroup.shippingMethod.shippingAmount : 0.0);

export const sumCosts = lineItem => sumValues(lineItem) + getShippingAmount(lineItem) + sumFees(lineItem);
export const sumQuantity = lineItems => lineItems.reduce((sum, lineItem) => sum + parseInt(lineItem.quantity, 10), 0);
export const sumFeeQuantity = lineItems => lineItems.reduce(
    (sum, lineItem) => (
        (getCardFee(lineItem) && (getCardFee(lineItem) !== parseFloat(0)))
            ? sum + parseInt(lineItem.quantity, 10)
            : sum
    )
    , 0);


export const sumShipping = lineItems => lineItems.reduce((sum, lineItem) => sum + getShippingAmount(lineItem), 0);

export const sumValuesTotal = lineItems => lineItems.reduce((sum, lineItem) => sum + sumValues(lineItem), 0);
export const sumTotalCost = lineItems => lineItems.reduce((sum, lineItem) => sum + sumCosts(lineItem), 0);

export const hasShippingMethodTokens = lineItems => lineItems.map(val => val.shippingGroup.shippingMethodToken).indexOf("") === -1;

/**
 * Determines if any line items have a multiplier associated with them
 */
export const hasMultiplier = lineItems => lineItems.some(val => (val.product && val.product.loyalty_multiplier && val.product.loyalty_multiplier !== 'undefined' &&
            parseInt(val.product.loyalty_multiplier, 10) > 0));

// Selectors
/**
 * Get the line item hash map as it is stored in the state.
 *
 * The line items are stored in a hashmap with a UID that is generated
 * the first time the line item is added to the store. This UID is set
 * as the value of the line item 'attribute. THis provides 0(1) access
 * to line items on the store. Although there exists Map and HashMap
 * types this returns an Object type with the structure of a hash map.
 *s
 * @returns {object} {id0: {lineItem0}, id1: {lineItem1}, ...}
 */
export const lineItemsHashMap = state => state.lineItems;
export const itemForKey = (state, lineItemKey) => {
    const lineItemForKey = lineItemsHashMap(state)[lineItemKey];
    if(typeof lineItemForKey === "undefined") return undefined;
    return {
        ...lineItemForKey,
        shippingGroup:
            lineItemForKey.deliveryMethod === 'PHYSICAL'
                ? shippingGroupForId(state, lineItemForKey.shippingGroupId)
                : undefined
    };
};
export const selectLineItems = state => (
    lineItemsHashMap(state) ? Object.keys(lineItemsHashMap(state)).map(key => itemForKey(state, key)) : []
);
export const selectDigitalLineItems = createSelector(
    selectLineItems,
    filterDigital
);
export const selectPhysicalLineItems = createSelector(
    selectLineItems,
    filterPhysical
);
export const getTotalQuantity = createSelector(
    selectLineItems,
    sumQuantity
);
export const getFeeTotalQuantity = createSelector(
    selectLineItems,
    sumFeeQuantity
);
export const getDigitalQuantity = createSelector(
    selectDigitalLineItems,
    sumQuantity
);
export const getPhysicalQuantity = createSelector(
    selectPhysicalLineItems,
    sumQuantity
);
export const getTotalShipping = createSelector(
    selectPhysicalLineItems,
    sumShipping
);
export const getTotalValue = createSelector(
    selectLineItems,
    sumValuesTotal
);
export const getDigitalValue = createSelector(
    selectDigitalLineItems,
    sumValuesTotal
);
export const getPhysicalValue = createSelector(
    selectPhysicalLineItems,
    sumValuesTotal
);
export const getGrandTotal = createSelector(
    selectLineItems,
    sumTotalCost
);

export const getTotalFees = createSelector(
    selectLineItems,
    sumTotalFees
);

export const itemsHaveShipping = createSelector(
    selectPhysicalLineItems,
    hasShippingMethodTokens
);

export const lineItemMultiplierExists = createSelector(
    selectLineItems,
    hasMultiplier
);
